import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../services/api";

function SignupPage() {
  const [formData, setFormData] = useState({
    full_name: "", email: "", phone_number: "",
    password: "", password2: "", date_of_birth: "", gender: "", role: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [pwMatch, setPwMatch] = useState(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const updated = { ...formData, [e.target.name]: e.target.value };
    setFormData(updated);
    if (e.target.name === "password" || e.target.name === "password2") {
      const p = e.target.name === "password" ? e.target.value : updated.password;
      const p2 = e.target.name === "password2" ? e.target.value : updated.password2;
      if (p2) setPwMatch(p === p2);
      else setPwMatch(null);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    const { full_name, email, phone_number, password, password2, date_of_birth, gender, role } = formData;
    if (!full_name || !email || !phone_number || !password || !password2 || !date_of_birth || !gender || !role) {
      setError("All fields are required. Please fill in every field.");
      return;
    }
    if (password !== password2) { setError("Passwords do not match."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      await api.post("register/", {
        full_name: full_name.trim(), email: email.trim(),
        phone_number: phone_number.trim(), password, password2,
        date_of_birth, gender, role
      });
      setLoading(false);
      navigate("/login");
    } catch (err) {
      setLoading(false);
      if (err.response?.data) {
        const errors = err.response.data;
        const msg = Object.entries(errors).map(([, v]) => Array.isArray(v) ? v.join(", ") : v).join(". ");
        setError(msg || "Registration failed. Please try again.");
      } else {
        setError("Unable to create account. Please try again.");
      }
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body {
          font-family: 'Inter', sans-serif; min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 1rem;
          background: linear-gradient(135deg, rgba(0,60,50,0.88) 0%, rgba(0,137,123,0.80) 100%),
            url('https://images.unsplash.com/photo-1579154204601-01588f351e67?w=1600&auto=format&fit=crop&q=80') center/cover no-repeat fixed;
        }
        .page-wrap { display: flex; width: 100%; max-width: 920px; border-radius: 20px; overflow: hidden; box-shadow: 0 24px 80px rgba(0,0,0,0.45); }
        .left { width: 300px; flex-shrink: 0; padding: 2.5rem 2rem; background: rgba(255,255,255,0.06); backdrop-filter: blur(12px); border-right: 1px solid rgba(255,255,255,0.1); display: flex; flex-direction: column; justify-content: space-between; }
        .logo-row { display: flex; align-items: center; gap: 10px; margin-bottom: 1.75rem; }
        .logo-name { font-size: 15px; font-weight: 700; color: #fff; line-height: 1.1; }
        .logo-sub { font-size: 10px; color: rgba(255,255,255,0.45); font-weight: 400; }
        .left-title { font-size: 22px; font-weight: 700; color: #fff; line-height: 1.3; margin-bottom: 10px; }
        .left-desc { font-size: 12.5px; color: rgba(255,255,255,0.55); line-height: 1.7; margin-bottom: 1.75rem; }
        .step-list { display: flex; flex-direction: column; gap: 12px; }
        .step-item { display: flex; align-items: flex-start; gap: 10px; }
        .step-num { width: 24px; height: 24px; border-radius: 50%; background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.2); display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; color: #fff; flex-shrink: 0; }
        .step-title { font-size: 12.5px; font-weight: 600; color: #fff; margin-bottom: 2px; }
        .step-sub { font-size: 11px; color: rgba(255,255,255,0.4); }
        .trust-row { display: flex; align-items: center; gap: 7px; font-size: 11px; color: rgba(255,255,255,0.35); }
        .trust-dot { width: 5px; height: 5px; border-radius: 50%; background: #4ade80; flex-shrink: 0; }
        .compliance-badges { display: flex; gap: 6px; margin-top: 8px; flex-wrap: wrap; }
        .badge { font-size: 10px; font-weight: 600; padding: 3px 8px; border-radius: 20px; background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); color: rgba(255,255,255,0.7); }
        .right { flex: 1; background: #fff; padding: 2rem 2rem; overflow-y: auto; }
        .form-head { margin-bottom: 1.25rem; }
        .form-head h2 { font-size: 20px; font-weight: 700; color: #0f172a; margin-bottom: 3px; }
        .form-head p { font-size: 12px; color: #64748b; }
        .prog-bar { display: flex; gap: 4px; margin-bottom: 1.25rem; }
        .prog { flex: 1; height: 3px; border-radius: 2px; background: #e2e8f0; transition: background 0.3s; }
        .prog.active { background: #00897B; }
        .prog.done { background: #16a34a; }
        .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .field { margin-bottom: 10px; }
        .field label { display: block; font-size: 11px; font-weight: 500; color: #475569; margin-bottom: 5px; }
        .input-wrap { position: relative; display: flex; align-items: center; }
        .input-wrap svg.ico { position: absolute; left: 10px; width: 13px; height: 13px; stroke: #94a3b8; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; pointer-events: none; }
        .input-wrap input, .input-wrap select { width: 100%; height: 38px; padding: 0 10px 0 32px; font-size: 12px; border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc; color: #0f172a; outline: none; transition: border-color 0.15s, box-shadow 0.15s, background 0.15s; appearance: none; }
        .input-wrap select, .input-wrap input[type="date"] { padding-left: 10px; }
        .input-wrap input:focus, .input-wrap select:focus { border-color: #00897B; box-shadow: 0 0 0 3px rgba(0,137,123,0.1); background: #fff; }
        .toggle-pw { position: absolute; right: 8px; background: none; border: none; cursor: pointer; padding: 3px; display: flex; align-items: center; }
        .toggle-pw svg { width: 13px; height: 13px; stroke: #94a3b8; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
        .hint { font-size: 11px; color: #94a3b8; margin-top: 3px; }
        .pw-ok { font-size: 11px; color: #16a34a; margin-top: 3px; }
        .pw-no { font-size: 11px; color: #dc2626; margin-top: 3px; }
        .check-row { display: flex; align-items: flex-start; gap: 7px; margin: 10px 0 12px; font-size: 11px; color: #64748b; }
        .check-row input { accent-color: #00897B; width: 13px; height: 13px; margin-top: 1px; flex-shrink: 0; }
        .check-row a { color: #00897B; text-decoration: none; font-weight: 500; }
        .btn-primary { width: 100%; height: 40px; background: #00897B; color: #fff; border: none; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 7px; transition: background 0.15s; margin-bottom: 8px; }
        .btn-primary:hover { background: #00695C; }
        .btn-primary:disabled { opacity: 0.7; cursor: not-allowed; }
        .btn-outline { width: 100%; height: 40px; background: #f8fafc; color: #334155; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; justify-content: center; text-decoration: none; transition: background 0.15s; }
        .btn-outline:hover { background: #f1f5f9; }
        .divider { display: flex; align-items: center; gap: 8px; margin: 8px 0; color: #cbd5e1; font-size: 11px; }
        .divider::before, .divider::after { content: ''; flex: 1; height: 1px; background: #e2e8f0; }
        .alert-err { background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 9px 11px; font-size: 11px; color: #dc2626; margin-bottom: 10px; display: flex; align-items: center; gap: 7px; }
        .spinner { width: 13px; height: 13px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: spin 0.6s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 700px) { .left { display: none; } .right { padding: 1.5rem; } .grid2 { grid-template-columns: 1fr; } }
      `}</style>

      <div className="page-wrap">
        <div className="left">
          <div>
            <div className="logo-row">
              <img src="/micronlabs.png" alt="MicronLabs Logo" style={{ width:"38px", height:"38px", borderRadius:"9px", objectFit:"contain", flexShrink:0 }}/>
              <div>
                <div className="logo-name">MicronLabs</div>
                <div className="logo-sub">Laboratory Information System</div>
              </div>
            </div>
            <div className="left-title">Join the MicronLabs network</div>
            <p className="left-desc">Create your account to access the full MicronLabs LIMS platform — designed for US healthcare providers, CLIA-certified laboratories and their patients.</p>
            <div className="step-list">
              {[
                { n:"1", title:"Create your account", sub:"Enter your details and set a secure password" },
                { n:"2", title:"Select your role", sub:"Patient, physician, technician or admin" },
                { n:"3", title:"Access the portal", sub:"Full access to your dashboard immediately" },
              ].map((s, i) => (
                <div key={i} className="step-item">
                  <div className="step-num">{s.n}</div>
                  <div>
                    <div className="step-title">{s.title}</div>
                    <div className="step-sub">{s.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="trust-row"><span className="trust-dot"></span>Secure · Encrypted · US-based infrastructure</div>
            <div className="compliance-badges">
              <span className="badge">HIPAA</span><span className="badge">CLIA</span><span className="badge">SOC 2</span><span className="badge">HL7</span>
            </div>
          </div>
        </div>

        <div className="right">
          <div className="form-head">
            <h2>Create your account</h2>
            <p>Fill in your details to get started</p>
          </div>
          <div className="prog-bar">
            <div className="prog done"/>
            <div className={`prog ${formData.password.length >= 8 ? "done" : "active"}`}/>
            <div className={`prog ${pwMatch === true ? "active" : ""}`}/>
          </div>
          {error && (
            <div className="alert-err">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {error}
            </div>
          )}
          <form onSubmit={handleSignup}>
            <div className="grid2">
              <div className="field">
                <label>Full name</label>
                <div className="input-wrap">
                  <svg className="ico" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  <input type="text" name="full_name" placeholder="John Smith" value={formData.full_name} onChange={handleChange} required disabled={loading}/>
                </div>
              </div>
              <div className="field">
                <label>Phone number</label>
                <div className="input-wrap">
                  <svg className="ico" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.0 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                  <input type="tel" name="phone_number" placeholder="+1 (555) 000-0000" value={formData.phone_number} onChange={handleChange} required disabled={loading}/>
                </div>
              </div>
            </div>
            <div className="field">
              <label>Email address</label>
              <div className="input-wrap">
                <svg className="ico" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                <input type="email" name="email" placeholder="you@organization.com" value={formData.email} onChange={handleChange} required disabled={loading}/>
              </div>
            </div>
            <div className="grid2">
              <div className="field">
                <label>Role</label>
                <div className="input-wrap">
                  <select name="role" value={formData.role} onChange={handleChange} required disabled={loading} style={{paddingLeft:"10px"}}>
                    <option value="">Select role</option>
                    <option value="admin">Administrator</option>
                    <option value="doctor">Physician</option>
                    <option value="technician">Lab technician</option>
                    <option value="patient">Patient</option>
                    <option value="facility">Facility client</option>
                  </select>
                </div>
              </div>
              <div className="field">
                <label>Gender</label>
                <div className="input-wrap">
                  <select name="gender" value={formData.gender} onChange={handleChange} required disabled={loading} style={{paddingLeft:"10px"}}>
                    <option value="">Select gender</option>
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                    <option value="O">Prefer not to say</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="field">
              <label>Date of birth</label>
              <div className="input-wrap">
                <input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} required disabled={loading} style={{paddingLeft:"10px"}}/>
              </div>
            </div>
            <div className="field">
              <label>Password</label>
              <div className="input-wrap">
                <svg className="ico" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                <input type={showPassword ? "text" : "password"} name="password" placeholder="At least 8 characters" value={formData.password} onChange={handleChange} required minLength="8" disabled={loading}/>
                <button type="button" className="toggle-pw" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <svg viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> : <svg viewBox="0 0 24 24"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>}
                </button>
              </div>
              <p className="hint">Must be at least 8 characters</p>
            </div>
            <div className="field">
              <label>Confirm password</label>
              <div className="input-wrap">
                <svg className="ico" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                <input type={showPassword2 ? "text" : "password"} name="password2" placeholder="Re-enter your password" value={formData.password2} onChange={handleChange} required disabled={loading}/>
                <button type="button" className="toggle-pw" onClick={() => setShowPassword2(!showPassword2)}>
                  {showPassword2 ? <svg viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> : <svg viewBox="0 0 24 24"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>}
                </button>
              </div>
              {pwMatch === true && <p className="pw-ok">Passwords match</p>}
              {pwMatch === false && <p className="pw-no">Passwords do not match</p>}
            </div>
            <div className="check-row">
              <input type="checkbox" id="terms" required disabled={loading}/>
              <label htmlFor="terms">I agree to the <Link to="/terms">Terms of Service</Link>, <Link to="/privacy">Privacy Policy</Link> and <Link to="/hipaa">HIPAA Notice of Privacy Practices</Link></label>
            </div>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading && <span className="spinner"/>}
              {loading ? "Creating account..." : "Create account"}
            </button>
            <div className="divider">or</div>
            <Link to="/login" className="btn-outline">Sign in instead</Link>
          </form>
        </div>
      </div>
    </>
  );
}

export default SignupPage;